/* eslint-disable no-param-reassign */
import { prop, fromPairs, pipe, pick, values, map, omit, path } from 'ramda'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import sift from 'sift'
import styled from 'styled-components'
import { CollapsibleBox } from '@/components/collapsible'
import { Input } from '@/components/form'
import { PageTitle, SectionHeader } from '@/components/headings'
import Section from '@/components/section'
import useAct from '@/hooks/use-act'
import { colors, fontSizes, fontWeights } from '@/styles'
import formatDate from '@/utils/format-date'
import formatLeaseType from '@/utils/format-lease-type'
import Context from './view/sign/context'
import Signatorees from './signatorees'
import {
  RentInfo,
  Amenities,
  PetPolicy,
  ParkingInfo,
  StorageInfo,
  ExtraFees,
  TenancyAgreement
} from './sections'
import renderTextField from './render-text-field'
import renderCustomField from './render-custom-field'
import Addendums from './addendums'

const completedDate = date => (date ? formatDate(date, 'YYYY-MM-DD') : '(Completed Date)')

const TitleSection = styled.section`
  h1 {
    margin-bottom: 50px;
  }

  margin-bottom: 60px;
`

const DateText = styled.p`
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.2px;
  line-height: 22px;
`

const SubSectionWrapper = styled.div`
  word-break: break-word;

  > :not(:last-child) {
    margin-bottom: 20px;
  }
`

const Title = styled.h3`
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.2px;
  line-height: 22px;
  margin: 0;
  position: relative;
`

const RequiredBadge = styled.p`
  margin: 0 0 0 auto;
  padding: 6px 12px;
  background: ${colors.primary};
  border-radius: 6px;
  color: ${colors.white};
  font-size: 11px;
  line-height: 15px;
`

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  p {
    margin: 0;
    font-weight: ${fontWeights.medium};
  }
`

const StyledCheckbox = styled(Input)`
  margin: 0 0 0 10px;
`

const SubSection = ({ item, children }) => {
  const { t } = useTranslation()

  // Hide section if none of it's inner fields exist
  if (children.every(c => c === null)) return null

  return (
    <SubSectionWrapper>
      <Title>{t(`b.contract.view.${item.title}.subtitle`)}</Title>
      {children}
    </SubSectionWrapper>
  )
}

const ReviewDetails = ({ contract, userType, isBillingUser, partialView, isComplete }) => {
  const { t } = useTranslation()
  const { data: act = {} } = useAct(contract.listing_contract_act_id)
  const unitScope = useSelector(
    path(['listing', contract.listingId, 'unit', 'unit_type_scope_txt_id'])
  )
  const contractComplete = contract.is_complete
  const { checkboxes, setCheckboxes } = useContext(Context)

  let features = contract.features
  //shared bathrooms need to be listed in features
  if (
    unitScope !== 'entire' &&
    contract.unit.count_full_shared_bathrooms &&
    parseFloat(contract.unit.count_full_shared_bathrooms) > 0
  ) {
    const dummyBathroomFeature = {
      name: 'Bathroom',
      txt_id: 'bathroom',
      types: {
        private: parseFloat(contract.unit.count_full_bathrooms || '0'),
        shared: parseFloat(contract.unit.count_full_shared_bathrooms)
      }
    }
    features = [dummyBathroomFeature, ...features]
  }

  const checkConditions = item => {
    if (!item.conditions) return item

    const customFieldValues = fromPairs(
      map(pipe(pick(['field_txt_id', 'field_value']), values))(contract.custom_fields)
    )
    const standardFieldValues = omit(['custom_fields'], contract)
    const additionalFields = {
      user_role: userType
    }
    const allCurrentValues = {
      ...customFieldValues,
      ...standardFieldValues,
      ...additionalFields
    }

    const cond = sift(item.conditions)
    const result = cond(allCurrentValues)

    return result ? item : null
  }

  const renderCheckboxConfirmation = name => {
    if (partialView || contract.state_machine === 'draft') return null

    if (typeof checkboxes[name] === 'undefined') {
      setCheckboxes(boxes => ({ ...boxes, [name]: false }))
    }

    return (
      <StyledCheckbox
        key={name}
        onChange={e => {
          setCheckboxes(prev => ({ ...prev, [name]: e.target.checked }))
        }}
        checked={checkboxes[name] || false}
        type="checkbox"
      />
    )
  }

  const renderLabel = (confirmations = []) => {
    if (partialView || contract.state_machine === 'draft') return null

    const requiredList = confirmations.reduce((accum, confirmation) => {
      const passesConditions = checkConditions(confirmation)
      return passesConditions ? [...accum, confirmation.required && !contractComplete] : accum
    }, [])

    const hasRequiredConfirmation = requiredList.length > 0 && requiredList.some(Boolean)

    return hasRequiredConfirmation ? (
      <RequiredBadge>{t('common.required', 'Required')}</RequiredBadge>
    ) : null
  }

  const renderFooter = ({ title, confirmations = [] }) => {
    const activeConfirmations = confirmations.filter(confirmation => checkConditions(confirmation))

    if (activeConfirmations.length === 0) return null

    return activeConfirmations.map(confirmation => {
      const text = prop('text', confirmation)
      return (
        <Wrapper key={text}>
          <p>{text}</p>
          {confirmation.required && !contractComplete && renderCheckboxConfirmation(title)}
        </Wrapper>
      )
    })
  }

  const renderLayout = (items, level = 1) => {
    if (!items) return null
    return items.map(item => renderLayoutItem(item, level))
  }

  const renderSection = (item, level) => {
    switch (item.title) {
      case 'rent_info':
        return (
          <RentInfo
            key={item.title}
            item={item}
            contract={contract}
            userType={userType}
            isBillingUser={isBillingUser}
            editable={!partialView && !isComplete}
          />
        )
      default:
        switch (level) {
          case 1:
            return (
              <Section key={item.title}>
                <SectionHeader>{t(`b.contract.view.${item.title}.title`)}</SectionHeader>
                {renderLayout(item.children, ++level)}
              </Section>
            )
          case 2:
            return (
              <CollapsibleBox
                key={item.title}
                title={t(`b.contract.view.${item.title}.subtitle`)}
                headerToggle={renderLabel(item.confirmations)}
                footer={renderFooter(item)}
                wrapChildren
              >
                {renderLayout(item.children, ++level)}
              </CollapsibleBox>
            )
          case 3:
            return (
              <SubSection key={item.title} item={item}>
                {renderLayout(item.children, ++level)}
              </SubSection>
            )
          default:
            return renderLayout(item.children, ++level)
        }
    }
  }

  const renderStandardField = item => {
    const standardFields = {
      lease_type: renderTextField({
        item,
        contract,
        value: formatLeaseType(contract.lease_type, t)
      }),
      start_date: renderTextField({
        item,
        contract,
        value: formatDate(contract.tenancyStartDate, 'YYYY/MM/DD')
      }),
      end_date: renderTextField({
        item,
        contract,
        value: formatDate(contract.tenancyEndDate, 'YYYY/MM/DD')
      }),
      lease_end_action: null,
      strata_lot_number: renderTextField({ item, contract }),
      strata_plan_number: renderTextField({ item, contract }),
      security_deposit: renderTextField({ item, contract, type: 'currency' }),
      security_deposit_due_date: renderTextField({
        item,
        contract,
        type: 'date'
      }),
      contract_features: (
        <Amenities
          key={item.name}
          header={t('b.contract.view.features.subtitle', 'Features')}
          amenities={features}
          customAmenities={contract.customFeatures}
          unitScope={unitScope}
          translationContext="basic:b.listing.unit_features"
        />
      ),
      contract_utilities: (
        <Amenities
          key={item.name}
          header={t('b.contract.view.utilities.subtitle', 'Utilities')}
          amenities={contract.utilities}
          customAmenities={contract.customUtilities}
          unitScope={unitScope}
          translationContext="basic:b.listing.utilities"
        />
      ),
      pet_policy: <PetPolicy key={item.name} contract={contract} />,
      parking_info: <ParkingInfo key={item.name} parkingFee={contract.parking_fee} />,
      storage_info: <StorageInfo key={item.name} storageFee={contract.storage_fee} />,
      extra_fees: <ExtraFees key={item.name} additionalFees={contract.additional_fees} />
    }
    return standardFields[item.name]
  }

  const renderLayoutItem = (item, level) => {
    const layoutItem = checkConditions(item)
    if (!layoutItem) return null

    switch (item.type) {
      case 'SECTION':
        return renderSection(item, level)
      case 'FIELD':
        return renderStandardField(item)
      case 'CUSTOM_FIELD':
        return renderCustomField(item, contract)
      default:
        return null
    }
  }

  return (
    <>
      <TitleSection>
        <PageTitle key="page-title">
          {t(
            'b.contract.view.residential_tenancy_agreement.title',
            'Residential Tenancy Agreement'
          )}
        </PageTitle>
        <DateText>
          {t('b.contract.view.effective_date.text', 'Effective Date:')}{' '}
          {completedDate(contract.effective_date)}
        </DateText>
        <p>
          {t(
            'b.contract.view.agreement_description.text',
            'THIS AGREEMENT made as of the Effective Date is entered into between the Landlord(s) and the Tenant(s), each as defined below in the basic terms ("Basic Terms") of this Residential Tenancy Agreement ("Agreement"), who, for good and valuable consideration, the receipt and sufficiency of which is mutually acknowledged, agree to be bound as follows:'
          )}
        </p>
      </TitleSection>
      <Signatorees
        landlords={contract.landlords}
        signingTenants={contract.signingTenants}
        nonSigningTenants={contract.nonSigningTenants}
        extraRenters={contract.extra_users}
        signatures={contract.signatures}
        company={contract.company}
        building={contract.building}
        unit={contract.unit}
        partialView={partialView}
      />
      {renderLayout(act.view_layout)}
      <TenancyAgreement act={act} contract={contract} />
      <Addendums contract={contract} userType={userType} signatures={contract.signatures} />
    </>
  )
}

ReviewDetails.defaultProps = {
  userType: null,
  partialView: false
}

export default ReviewDetails
/* eslint-enable no-param-reassign */
